import React, { useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { useVisualizationStore } from '../store/visualizationStore';
import { VisualizationNode, VisualizationLink, getLinkEndpointId } from '../types/visualization';
import { AutoLayoutEngine, AutoLayoutConfig } from '../utils/autoLayout';
import { ComponentLayoutEngine, ComponentConnection } from '../utils/componentLayout';
import { countExternalNetworkInterfaces } from '../utils/externalInterfaceCount';
import { shouldUseTorusLayout, applyTorusLayout } from '../utils/torusLayout';

const normalizeHighlightValue = (value?: string | null): string | null => {
  if (value === undefined || value === null) return null;
  const normalized = value.toString().trim().toLowerCase();
  return normalized || null;
};

const expandValueVariants = (value?: string | null): string[] => {
  const normalized = normalizeHighlightValue(value);
  if (!normalized) return [];

  const variants = new Set<string>();
  variants.add(normalized);

  if (normalized.includes('[')) {
    variants.add(normalized.slice(0, normalized.indexOf('[')));
  }

  normalized.split(/[./]/).forEach(part => {
    const trimmed = part.trim();
    if (trimmed) {
      variants.add(trimmed);
    }
  });

  normalized.split(/\s+/).forEach(part => {
    const trimmed = part.trim();
    if (trimmed) {
      variants.add(trimmed);
    }
  });

  return Array.from(variants);
};

const gatherNodeHighlightValues = (node: VisualizationNode): string[] => {
  const values = new Set<string>();
  const add = (raw?: string | null) => {
    expandValueVariants(raw).forEach(value => values.add(value));
  };

  add(node.id);
  add(node.name);
  add(node.parent);

  if (node.metadata) {
    add(node.metadata.deviceType);
    add(node.metadata.componentType);
    add(node.metadata.componentChoice);
    if (node.metadata.location) {
      add(node.metadata.location.site);
      add(node.metadata.location.pod);
      add(node.metadata.location.network);
      add(node.metadata.location.rack);
    }
  }

  return Array.from(values);
};

const gatherLinkHighlightValues = (link: VisualizationLink): string[] => {
  const values = new Set<string>();
  const add = (raw?: string | null) => {
    expandValueVariants(raw).forEach(value => values.add(value));
  };

  add(link.protocol);
  add(link.metadata?.linkName);
  add(link.metadata?.linkType);

  return Array.from(values);
};

interface VisualizationEngineProps {
  width: number;
  height: number;
}

export const VisualizationEngine: React.FC<VisualizationEngineProps> = ({ width, height }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const simulationRef = useRef<d3.Simulation<any, any> | null>(null);
  const simulationIdRef = useRef<number>(0); // Track which simulation is current
  const visibleNodesRef = useRef<VisualizationNode[]>([]);
  const visibleLinksRef = useRef<VisualizationLink[]>([]);
  const nodeSelectionRef = useRef<d3.Selection<SVGCircleElement, VisualizationNode, SVGGElement, unknown> | null>(null);
  const linkSelectionRef = useRef<d3.Selection<SVGLineElement, VisualizationLink, SVGGElement, unknown> | null>(null);
  const labelSelectionRef = useRef<d3.Selection<SVGTextElement, VisualizationNode, SVGGElement, unknown> | null>(null);
  const linkLabelSelectionRef = useRef<d3.Selection<SVGTextElement, VisualizationLink, SVGGElement, unknown> | null>(null);

  const {
    nodes,
    links,
    viewport,
    selectedNode,
    showNodeTypes,
    showConnectionTypes,
    currentContext,
    autoLayoutType,
    highlightFixedNodes,
    setViewport,
    selectNode,
    drillDown,
    editorHighlights,
    editorHighlightEnabled,
    updateNodeLayout,
    setRenderedPositions,
    setSimulationRunning
  } = useVisualizationStore();
  const latestHighlightsRef = useRef(editorHighlights);

  // Node color mapping based on type
  const getNodeColor = (node: VisualizationNode): string => {
    // If highlighting fixed nodes is enabled, show fixed nodes in sky blue
    if (highlightFixedNodes && (node.fx !== undefined || node.fy !== undefined)) {
      return '#0ea5e9'; // Sky-500 - indicates manually positioned nodes
    }

    const colors = {
      site: '#1f77b4',      // Blue
      pod: '#ff7f0e',       // Orange
      network: '#17becf',   // Cyan
      rack: '#2ca02c',      // Green
      device: '#d62728',    // Red
      component: '#9467bd'  // Purple
    };
    return colors[node.type] || '#gray';
  };

  // Link color mapping based on protocol
  const getLinkColor = useCallback((link: VisualizationLink): string => {
    const colors: Record<string, string> = {
      'Ethernet': '#999',         // Lighter gray - network infrastructure
      'InfiniBand': '#777',       // Darker gray - network infrastructure
      'NVLink': '#76b900',        // NVIDIA green - device internal
      'PCIe': '#0891b2',          // Teal - device internal
      'Custom': '#eab308',        // Yellow - device internal
      'Unknown': '#6b7280'        // Medium gray
    };
    return colors[link.protocol || 'Unknown'] || '#999';
  }, []);

  const applyHighlights = useCallback((highlights: { nodeTokens: string[]; linkTokens: string[] }) => {
    const nodeSelection = nodeSelectionRef.current;
    const linkSelection = linkSelectionRef.current;
    const labelSelection = labelSelectionRef.current;
    const linkLabelSelection = linkLabelSelectionRef.current;

    const visibleNodes = visibleNodesRef.current;
    const visibleLinks = visibleLinksRef.current;

    if (!nodeSelection || !linkSelection || !visibleNodes.length) {
      return;
    }

    const nodeTokenSet = new Set(highlights.nodeTokens);
    const linkTokenSet = new Set(highlights.linkTokens);
    const highlightedNodeIds = new Set<string>();
    const strongLinkHighlights = new Set<string>();

    if (nodeTokenSet.size) {
      visibleNodes.forEach(node => {
        const nodeValues = gatherNodeHighlightValues(node);
        if (nodeValues.some(value => nodeTokenSet.has(value))) {
          highlightedNodeIds.add(node.id);
        }
      });
    }

    if (linkTokenSet.size) {
      visibleLinks.forEach(link => {
        const linkValues = gatherLinkHighlightValues(link);
        if (linkValues.some(value => linkTokenSet.has(value))) {
          strongLinkHighlights.add(link.id);
        }
      });
    }

    if (strongLinkHighlights.size) {
      visibleLinks.forEach(link => {
        if (strongLinkHighlights.has(link.id)) {
          highlightedNodeIds.add(getLinkEndpointId(link.source));
          highlightedNodeIds.add(getLinkEndpointId(link.target));
        }
      });
    }

    const connectedLinkIds = new Set<string>(strongLinkHighlights);
    if (highlightedNodeIds.size) {
      visibleLinks.forEach(link => {
        const sourceId = getLinkEndpointId(link.source);
        const targetId = getLinkEndpointId(link.target);
        if (highlightedNodeIds.has(sourceId) || highlightedNodeIds.has(targetId)) {
          connectedLinkIds.add(link.id);
        }
      });
    }

    const dimNodes = highlightedNodeIds.size > 0;
    const dimLinks = connectedLinkIds.size > 0;

    nodeSelection
      .attr('fill-opacity', (d) => dimNodes && !highlightedNodeIds.has(d.id) ? 0.25 : 1)
      .attr('stroke', (d) => {
        if (highlightedNodeIds.has(d.id)) {
          return '#facc15';
        }
        return selectedNode === d.id ? '#000' : '#fff';
      })
      .attr('stroke-width', (d) => {
        if (highlightedNodeIds.has(d.id)) {
          return 4;
        }
        return selectedNode === d.id ? 3 : 1.5;
      });

    labelSelection?.attr('opacity', (d) => dimNodes && !highlightedNodeIds.has(d.id) ? 0.35 : 1);

    linkSelection
      .attr('stroke', (d) => {
        if (strongLinkHighlights.has(d.id)) {
          return '#fb923c';
        }
        if (connectedLinkIds.has(d.id)) {
          return '#facc15';
        }
        return getLinkColor(d);
      })
      .attr('stroke-opacity', (d) => {
        if (!dimLinks) {
          return 0.7;
        }
        return connectedLinkIds.has(d.id) ? 0.95 : 0.08;
      })
      .attr('stroke-width', (d) => {
        if (strongLinkHighlights.has(d.id)) {
          return 4;
        }
        if (connectedLinkIds.has(d.id)) {
          return 3;
        }
        return 2;
      });

    linkLabelSelection?.attr('opacity', (d) => dimLinks && !connectedLinkIds.has(d.id) ? 0.2 : 1);
  }, [getLinkColor, selectedNode]);

  useEffect(() => {
    const activeTokens = editorHighlightEnabled ? editorHighlights : { nodeTokens: [], linkTokens: [] };
    latestHighlightsRef.current = activeTokens;
    applyHighlights(activeTokens);
  }, [editorHighlights, editorHighlightEnabled, applyHighlights]);

  // Filter nodes and links based on hierarchical context and filters
  const getVisibleElements = useCallback(() => {
    // Start with nodes that match the current filters
    // Exclude rack nodes from rendering (they exist for navigation only)
    let visibleNodes = nodes.filter(node =>
      showNodeTypes.includes(node.type) && node.type !== 'rack'
    );

    // Apply hierarchical context filtering
    if (currentContext) {
      const contextNode = nodes.find(n => n.id === currentContext);
      const isNetworkContext = contextNode?.type === 'network';

      visibleNodes = visibleNodes.filter(node => {
        // Direct children are always visible
        if (node.parent === currentContext) {
          return true;
        }

        // For network contexts, also show devices that belong to racks within this network
        if (isNetworkContext && node.type === 'device') {
          // Check if the device's rack belongs to this network
          const deviceRackParent = node.parent; // This should be a rack ID
          const rackNode = nodes.find(n => n.id === deviceRackParent);
          if (rackNode && rackNode.parent === currentContext) {
            return true;
          }
        }

        return false;
      });
    } else {
      // At the root level, show only top-level nodes (sites)
      visibleNodes = visibleNodes.filter(node => {
        // For sites, they should not have a parent or parent should be null/undefined
        return node.type === 'site' || !node.parent;
      });
    }

    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

    // Helper function to find visible ancestor by walking up parent chain
    // Supports device-in-device composition where multiple parent levels may be hidden
    const findVisibleAncestor = (nodeId: string): string | null => {
      let current = nodes.find(n => n.id === nodeId);
      while (current && current.parent) {
        if (visibleNodeIds.has(current.parent)) {
          return current.parent;
        }
        const parentId = current.parent;
        current = nodes.find(n => n.id === parentId);
      }
      return null;
    };

    // Smart link filtering: aggregate connections to the appropriate hierarchical level
    const visibleLinks: VisualizationLink[] = [];
    const connectionCounts = new Map<string, {link: VisualizationLink, count: number, totalBandwidth: number}>();

    for (const link of links) {
      // Skip links that have a protocol but it's not in the show list
      // Allow links with no protocol to pass through
      if (link.protocol && !showConnectionTypes.includes(link.protocol)) {
        continue;
      }

      const originalSource = getLinkEndpointId(link.source);
      const originalTarget = getLinkEndpointId(link.target);
      let sourceNode = originalSource;
      let targetNode = originalTarget;

      // If the link endpoints are not visible, try to find their visible ancestors
      // Walk up the parent hierarchy recursively for device-in-device support
      if (!visibleNodeIds.has(sourceNode)) {
        const visibleAncestor = findVisibleAncestor(sourceNode);
        if (visibleAncestor) {
          sourceNode = visibleAncestor;
        } else {
          continue; // Can't find visible source
        }
      }

      if (!visibleNodeIds.has(targetNode)) {
        const visibleAncestor = findVisibleAncestor(targetNode);
        if (visibleAncestor) {
          targetNode = visibleAncestor;
        } else {
          continue; // Can't find visible target
        }
      }

      // Only count links that should be aggregated to show count
      // We need to be careful to only count the actual external connections, not internal ones
      let shouldCount = false;

      if (originalSource.includes('/') && originalTarget.includes('/')) {
        // Component-to-component connections: count if they cross devices
        const sourceDevice = originalSource.split('/')[0];
        const targetDevice = originalTarget.split('/')[0];
        const crossesDevices = sourceDevice !== targetDevice;

        // Additional check: only count if the aggregated link also crosses devices
        const aggregatedCrossesDevices = sourceNode !== targetNode;

        shouldCount = crossesDevices && aggregatedCrossesDevices;

        // console.log(`Component link ${originalSource} -> ${originalTarget}: shouldCount=${shouldCount}`);
      } else if (!originalSource.includes('/') && !originalTarget.includes('/')) {
        // Device-to-device connections: always count these
        shouldCount = true;
        // console.log(`Device link ${originalSource} -> ${originalTarget}: shouldCount=${shouldCount}`);
      } else {
        // Mixed (one device, one component): this shouldn't happen in our current data model
        shouldCount = false;
        // console.log(`Mixed link ${originalSource} -> ${originalTarget}: shouldCount=${shouldCount}`);
      }

      // Aggregate connections between the same nodes
      const connectionKey = `${sourceNode}-${targetNode}`;
      const reverseKey = `${targetNode}-${sourceNode}`;
      const useKey = connectionCounts.has(connectionKey) ? connectionKey :
                     connectionCounts.has(reverseKey) ? reverseKey : connectionKey;

      const linkBandwidth = link.bandwidth?.value || 0;

      if (connectionCounts.has(useKey)) {
        const existing = connectionCounts.get(useKey)!;
        if (shouldCount) {
          existing.count++;
          // console.log(`  Incrementing count for ${useKey} to ${existing.count}`);
        }
        existing.totalBandwidth += linkBandwidth;
      } else {
        const newCount = shouldCount ? 1 : 0;
        connectionCounts.set(useKey, {
          link: {
            ...link,
            source: sourceNode,
            target: targetNode,
            id: `aggregated-${connectionCounts.size}`
          },
          count: newCount,
          totalBandwidth: linkBandwidth
        });
        // console.log(`  Created new aggregation ${useKey} with count ${newCount}`);
      }
    }

    // Convert aggregated connections to visible links with count metadata
    for (const {link, count, totalBandwidth} of Array.from(connectionCounts.values())) {
      visibleLinks.push({
        ...link,
        metadata: {
          ...link.metadata,
          aggregatedCount: count,
          totalBandwidth: count > 1 ? totalBandwidth : undefined
        },
        // Update bandwidth to show total if aggregated
        bandwidth: count > 1 && link.bandwidth ? {
          ...link.bandwidth,
          value: totalBandwidth
        } : link.bandwidth
      });
    }

    // Calculate network interface counts for each visible node
    const visibleNodesWithCounts = visibleNodes.map(node => {
      // Count external network interfaces (NICs/ports) only
      const externalInterfaceCount = countExternalNetworkInterfaces(node, nodes, links);

      return {
        ...node,
        metadata: {
          ...node.metadata,
          externalComponentCount: externalInterfaceCount
        }
      };
    });

    return { visibleNodes: visibleNodesWithCounts, visibleLinks };
  }, [nodes, links, showNodeTypes, showConnectionTypes, currentContext]);

  // Node size mapping based on type and zoom level
  const getNodeSize = useCallback((node: VisualizationNode): number => {
    const baseSizes = {
      site: 20,
      pod: 15,
      network: 13,
      rack: 12,
      device: 10, // Increased from 8 to make device labels more visible
      component: 6
    };
    // Remove zoom factor since SVG transform handles scaling
    return baseSizes[node.type] || 8;
  }, []);

  // Initialize zoom behavior
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    // Create zoom behavior with better controls
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 10])
      .wheelDelta((event) => {
        // Reduce wheel sensitivity and add smooth scaling
        return -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002);
      })
      .filter((event) => {
        // Only allow zoom/pan with specific conditions
        return !event.ctrlKey && !event.button;
      })
      .on('start', (event) => {
        // Stop any running force simulation when user starts zooming/panning
        // Only stop if this is a user-initiated zoom (sourceEvent exists), not programmatic
        if (event.sourceEvent && simulationRef.current) {
          // Set alpha to 0 to stop the simulation immediately
          simulationRef.current.alpha(0);
          simulationRef.current.stop();
        }
      })
      .on('zoom', (event) => {
        // Always try to find the container, as it may be recreated
        const container = svg.select('.visualization-container');
        if (!container.empty()) {
          const { transform } = event;
          container.attr('transform', transform.toString());
        }
      })
      .on('end', (event) => {
        // Only update viewport state when zoom/pan ends to prevent infinite loops
        const container = svg.select('.visualization-container');
        if (!container.empty()) {
          const { transform } = event;
          setViewport({
            x: transform.x,
            y: transform.y,
            zoom: transform.k
          });
        }
      });

    svg.call(zoom);
    zoomRef.current = zoom;

    return () => {
      svg.on('.zoom', null);
    };
  }, [setViewport]);

  // Update zoom transform when viewport changes externally (but not during user interaction)
  useEffect(() => {
    if (!svgRef.current || !zoomRef.current) {
      console.log('Viewport effect: SVG or zoom not ready yet');
      return;
    }

    const svg = d3.select(svgRef.current);
    const currentTransform = d3.zoomTransform(svgRef.current);

    // Only update if there's a significant difference to avoid feedback loops
    const threshold = 0.001;
    const needsUpdate = Math.abs(currentTransform.x - viewport.x) > threshold ||
        Math.abs(currentTransform.y - viewport.y) > threshold ||
        Math.abs(currentTransform.k - viewport.zoom) > threshold;

    if (needsUpdate) {
      const transform = d3.zoomIdentity
        .translate(viewport.x, viewport.y)
        .scale(viewport.zoom);

      // Apply transform to SVG's zoom behavior
      // This updates the internal zoom state which will be correctly applied
      // to the visualization container even if it gets recreated
      svg.call(zoomRef.current.transform as any, transform);
    }
  }, [viewport.x, viewport.y, viewport.zoom]);

  // Apply autolayout with role-based positioning
  const applyAutoLayout = useCallback((visibleNodes: VisualizationNode[], visibleLinks: VisualizationLink[]): VisualizationNode[] => {
    if (!currentContext || visibleNodes.length < 2) {
      return visibleNodes; // Return nodes as-is when not enough nodes
    }

    // Check for 3D torus/cube topology first
    // shouldUseTorusLayout filters to device nodes internally, so safe to call here
    if (shouldUseTorusLayout(visibleNodes)) {
      return applyTorusLayout(visibleNodes, width, height);
    }

    // Skip AutoLayout only if ALL nodes have manual layout (pure manual mode)
    // For hybrid layouts (some nodes have coordinates, some don't), we still apply AutoLayout
    // to position unfixed nodes, and the layout will preserve fx/fy for fixed nodes
    const allNodesHaveManualLayout = visibleNodes.every(node => node.fx !== undefined && node.fy !== undefined);
    if (allNodesHaveManualLayout) {
      console.log('Pure manual layout detected - skipping AutoLayout for', visibleNodes.length, 'nodes');
      return visibleNodes; // Preserve manual coordinates
    }

    // Check if we're in component-level view (all nodes are components)
    const hasComponents = visibleNodes.some(node => node.type === 'component');
    const hasOnlyComponents = hasComponents && visibleNodes.every(node => node.type === 'component');

    // Use ComponentLayoutEngine for component-level views
    if (hasOnlyComponents) {
      // Convert links to ComponentConnection format
      const componentConnections: ComponentConnection[] = visibleLinks.map(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;

        // Extract component ID from full path (e.g., "host[0]/npu-0" -> "npu-0")
        const fromComponent = sourceId.split('/').pop() || sourceId;
        const toComponent = targetId.split('/').pop() || targetId;

        return {
          fromComponent,
          toComponent,
          linkType: link.protocol || link.metadata?.linkName
        };
      });

      const componentLayoutEngine = new ComponentLayoutEngine({
        baseSpacing: 80,
        centerX: width / 2,
        centerY: height / 2
      });

      return componentLayoutEngine.layoutComponents(visibleNodes, componentConnections);
    }

    // Use AutoLayoutEngine for device/network-level views
    const hasDevices = visibleNodes.some(node => node.type === 'device');
    if (!hasDevices || visibleNodes.length < 3) {
      return visibleNodes; // Return nodes as-is for non-device views
    }

    const autoLayoutConfig: AutoLayoutConfig = {
      enableVerticalSeparation: true,
      layerSpacing: height / 4,
      nodeSpacing: width / 10,
      forceStrength: 0.3,
      layoutType: autoLayoutType
    };

    const layoutEngine = new AutoLayoutEngine(visibleNodes, visibleLinks, autoLayoutConfig);
    return layoutEngine.applyRoleBasedLayout(width, height);
  }, [width, height, currentContext, autoLayoutType]);

  // Force simulation setup
  const setupForceSimulation = useCallback((visibleNodes: VisualizationNode[], visibleLinks: VisualizationLink[]) => {
    if (!visibleNodes.length) return null;

    // Apply role-based autolayout positioning
    const layoutedNodes = applyAutoLayout(visibleNodes, visibleLinks);

    // Update visible nodes with layout positions
    layoutedNodes.forEach((layoutedNode, index) => {
      if (visibleNodes[index]) {
        visibleNodes[index].x = layoutedNode.x;
        visibleNodes[index].y = layoutedNode.y;
      }
    });

    // Create hierarchical positioning for different node types
    const simulation = d3.forceSimulation(visibleNodes as any)
      .force('link', d3.forceLink(visibleLinks).id((d: any) => d.id).distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d: any) => getNodeSize(d) + 2));

    // Add hierarchy-specific forces based on current context
    if (!currentContext) {
      // At global level, spread out sites horizontally
      simulation.force('x', d3.forceX((d: any) => {
        if (d.type === 'site') return (visibleNodes.findIndex(n => n.id === d.id) + 1) * (width / (visibleNodes.filter(n => n.type === 'site').length + 1));
        return width / 2;
      }).strength(0.5));
    } else {
      // For device views with autolayout, add role-based forces
      const contextNode = nodes.find(n => n.id === currentContext);
      if (contextNode?.type === 'network') {
        // Apply vertical layering forces for network views
        simulation.force('y', d3.forceY((d: any) => {
          // Use the autolayout Y positions as targets
          return d.y;
        }).strength(0.8));

        // Reduce horizontal centering to allow role-based positioning
        simulation.force('center', d3.forceCenter(width / 2, height / 2).strength(0.1));
      }
    }

    return simulation;
  }, [width, height, currentContext, applyAutoLayout, nodes, getNodeSize]);

  // Render visualization content
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    // Remove existing content but keep zoom behavior
    svg.selectAll('.visualization-container').remove();

    const { visibleNodes, visibleLinks } = getVisibleElements();
    if (!visibleNodes.length) return;
    visibleNodesRef.current = visibleNodes;
    visibleLinksRef.current = visibleLinks;

    // Detect if this is a manual layout (nodes have fixed positions)
    // Hybrid mode: nodes with fx/fy coordinates stay fixed, nodes without use force simulation
    const hasFixedCoordinates = visibleNodes.some(node => node.fx !== undefined || node.fy !== undefined);
    const allNodesHaveCoordinates = visibleNodes.every(node => node.fx !== undefined && node.fy !== undefined);
    const hasManualLayout = hasFixedCoordinates; // Auto-detect based on presence of coordinates

    // Create container group
    const container = svg.append('g').attr('class', 'visualization-container');
    containerRef.current = container;

    // Apply current viewport transform immediately to new container
    // For manual layouts, use the stored viewport offset
    // For auto layouts, start at origin (0,0) and let force simulation center it
    const currentTransform = hasManualLayout
      ? d3.zoomIdentity.translate(viewport.x, viewport.y).scale(viewport.zoom)
      : d3.zoomIdentity.translate(0, 0).scale(viewport.zoom);
    container.attr('transform', currentTransform.toString());

    // Render links
    const linkGroup = container.append('g').attr('class', 'links');
    const linkElements = linkGroup.selectAll<SVGLineElement, VisualizationLink>('line')
      .data(visibleLinks)
      .enter().append('line')
      .attr('stroke', getLinkColor)
      .attr('stroke-opacity', 0.7)
      .attr('stroke-width', 2);
    linkSelectionRef.current = linkElements as d3.Selection<SVGLineElement, VisualizationLink, SVGGElement, unknown>;

    // Render nodes
    const nodeGroup = container.append('g').attr('class', 'nodes');
    const nodeElements = nodeGroup.selectAll<SVGCircleElement, VisualizationNode>('circle')
      .data(visibleNodes)
      .enter().append('circle')
      .attr('r', getNodeSize)
      .attr('fill', getNodeColor)
      .attr('stroke', (d) => selectedNode === d.id ? '#000' : '#fff')
      .attr('stroke-width', (d) => selectedNode === d.id ? 3 : 1.5)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        try {
          event.stopPropagation();

          // If clicking on a container node (site, pod, network, rack, device), drill down
          if (['site', 'pod', 'network', 'rack', 'device'].includes(d.type)) {
            drillDown(d.id);
          } else {
            selectNode(d.id === selectedNode ? null : d.id);
          }
        } catch (error) {
          console.warn('Click handler error:', error);
        }
      })
      .on('mouseenter', (event, d) => {
        // Remove hover state changes to prevent continuous redraw
      })
      .on('mouseleave', () => {
        // Remove hover state changes to prevent continuous redraw
      });
    nodeSelectionRef.current = nodeElements as d3.Selection<SVGCircleElement, VisualizationNode, SVGGElement, unknown>;

    // Add labels for nodes
    // Get the context node to determine if we're in a device view
    const contextNode = currentContext ? nodes.find(n => n.id === currentContext) : null;
    const isDeviceContext = contextNode?.type === 'device';

    // Precompute connection status for all visible nodes (avoids O(N·E) in hot loop)
    const nodeHasConnections = new Set<string>();
    for (const link of visibleLinks) {
      const sourceId = getLinkEndpointId(link.source);
      const targetId = getLinkEndpointId(link.target);
      nodeHasConnections.add(sourceId);
      nodeHasConnections.add(targetId);
    }

    // Calculate label anchor based on node position in hierarchical layouts
    const getLabelAnchor = (node: VisualizationNode): string => {
      // For nodes without connections, center the label below
      if (!nodeHasConnections.has(node.id)) {
        return 'middle';
      }

      // For non-device nodes or component views, center the label
      if (node.type !== 'device' || isDeviceContext) {
        return 'middle';
      }

      // For hierarchical device layouts, position labels on the outside edges
      const x = node.x || 0;
      const centerX = width / 2;

      // If node is on the left half, label goes to the left (outside) - use 'end'
      // If node is on the right half, label goes to the right (outside) - use 'start'
      return x < centerX ? 'end' : 'start';
    };

    const getLabelOffset = (node: VisualizationNode): {x: number, y: number} => {
      // For nodes without connections, place label below
      if (!nodeHasConnections.has(node.id)) {
        const nodeRadius = getNodeSize(node);
        return { x: 0, y: nodeRadius + 15 }; // 15px below node edge
      }

      const anchor = getLabelAnchor(node);

      // No offset for centered labels
      if (anchor === 'middle') {
        return { x: 0, y: 0 };
      }

      // Offset towards the outside edges for hierarchical layouts
      const x = node.x || 0;
      const centerX = width / 2;
      const nodeRadius = getNodeSize(node);
      const offset = nodeRadius + 10; // 10px padding from node edge

      // If node is on left, offset to the left (-), if on right, offset to the right (+)
      return { x: x < centerX ? -offset : offset, y: 0 };
    };

    const labelGroup = container.append('g').attr('class', 'labels');
    const nodesForLabels = visibleNodes.filter(d => getNodeSize(d) >= 6); // Include components (size 6)

    const labelElements = labelGroup.selectAll<SVGTextElement, VisualizationNode>('text')
      .data(nodesForLabels)
      .enter().append('text')
      .attr('dy', '.35em')
      .attr('font-size', (d) => d.type === 'component' ? '10px' : '14px') // Smaller font for components
      .attr('fill', '#333') // Gray
      .attr('stroke', '#fff') // Add white outline
      .attr('stroke-width', '1px')
      .attr('paint-order', 'stroke fill') // Outline first, then fill
      .attr('pointer-events', 'none')
      .text((d) => d.name)
      .each(function(d) {
        const offset = getLabelOffset(d);
        d3.select(this)
          .attr('text-anchor', getLabelAnchor(d))
          .attr('x', (d.x || 0) + offset.x)
          .attr('y', (d.y || 0) + offset.y);
      });
    labelSelectionRef.current = labelElements as d3.Selection<SVGTextElement, VisualizationNode, SVGGElement, unknown>;

    // Add labels for aggregated links (excluding self-loops which represent internal device connections)
    const linkLabelGroup = container.append('g').attr('class', 'link-labels');
    const linkLabelElements = linkLabelGroup.selectAll<SVGTextElement, VisualizationLink>('text')
      .data(visibleLinks.filter(d => {
        const sourceId = getLinkEndpointId(d.source);
        const targetId = getLinkEndpointId(d.target);
        return d.metadata?.aggregatedCount && d.metadata.aggregatedCount > 1 && sourceId !== targetId;
      }))
      .enter().append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '.35em')
      .attr('font-size', '10px')
      .attr('fill', '#666')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .attr('opacity', 1)
      .style('background', 'rgba(255, 255, 255, 0.8)')
      .text((d) => `×${d.metadata?.aggregatedCount}`);
    linkLabelSelectionRef.current = linkLabelElements as d3.Selection<SVGTextElement, VisualizationLink, SVGGElement, unknown>;

    applyHighlights(latestHighlightsRef.current);

    const pushRenderedPositions = (sourceNodes: VisualizationNode[]) => {
      const positionMap = new Map(
        sourceNodes.map(node => [node.id, { x: node.x ?? 0, y: node.y ?? 0 }])
      );
      setRenderedPositions(positionMap);
    };

    // Skip force simulation ONLY if ALL nodes have manual coordinates
    // For hybrid layouts (some nodes have coordinates, some don't), we need simulation
    // D3 will automatically keep nodes with fx/fy fixed while simulating others
    // But we still need to resolve link source/target IDs to node objects for manual-only layouts
    if (allNodesHaveCoordinates) {
      // Create a map of node IDs to nodes for quick lookup
      const nodeMap = new Map(visibleNodes.map(n => [n.id, n]));

      // Resolve link source/target strings to node objects
      visibleLinks.forEach(link => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
        const targetId = typeof link.target === 'string' ? link.target : link.target.id;

        const sourceNode = nodeMap.get(sourceId);
        const targetNode = nodeMap.get(targetId);

        if (sourceNode && targetNode) {
          link.source = sourceNode as any;
          link.target = targetNode as any;
        }
      });
    }

    // Setup force simulation with reduced movement
    // Only skip simulation if ALL nodes have manual coordinates (pure manual layout)
    // For hybrid layouts, simulation will position unfixed nodes while keeping fixed nodes in place
    const simulation = allNodesHaveCoordinates ? null : setupForceSimulation(visibleNodes, visibleLinks);
    simulationRef.current = simulation as any; // Store in ref for cleanup

    // Increment simulation ID to track this instance (even if null, for consistency)
    simulationIdRef.current += 1;
    const currentSimulationId = simulationIdRef.current;

    if (simulation) {
      // Mark simulation as running to disable fix button
      setSimulationRunning(true);
      // Capture initial positions so manual mode can snapshot even before simulation ends
      pushRenderedPositions(visibleNodes);

      // Simulation with moderate duration for better settling
      simulation
        .alpha(0.3) // Higher initial alpha for quick settling
        .alphaDecay(0.05) // Moderate decay - allows more settling time
        .velocityDecay(0.8) // Lower velocity decay for quicker settling
        .alphaTarget(0); // Ensure it stops completely

      simulation.on('tick', () => {
        linkElements
          .attr('x1', (d: any) => d.source.x)
          .attr('y1', (d: any) => d.source.y)
          .attr('x2', (d: any) => d.target.x)
          .attr('y2', (d: any) => d.target.y);

        nodeElements
          .attr('cx', (d: any) => d.x)
          .attr('cy', (d: any) => d.y);

        labelElements.each(function(d: any) {
          const offset = getLabelOffset(d);
          d3.select(this)
            .attr('text-anchor', getLabelAnchor(d))
            .attr('x', d.x + offset.x)
            .attr('y', d.y + offset.y);
        });

        linkLabelElements
          .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
          .attr('y', (d: any) => (d.source.y + d.target.y) / 2);
      });

      // Mark simulation as finished when it ends
      // Only update state if this is still the current simulation
      simulation.on('end', () => {
        if (currentSimulationId === simulationIdRef.current) {
          pushRenderedPositions(visibleNodes);
          setSimulationRunning(false);
        }
      });
    } else {
      // No simulation for manual layout
      setSimulationRunning(false);
      // Manual layout - set positions immediately without simulation
      nodeElements
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y);

      labelElements.each(function(d: any) {
        const offset = getLabelOffset(d);
        d3.select(this)
          .attr('text-anchor', getLabelAnchor(d))
          .attr('x', d.x + offset.x)
          .attr('y', d.y + offset.y);
      });

      linkElements
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      linkLabelElements
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2);

      // Store the rendered positions for manual capture on mode switch
      pushRenderedPositions(visibleNodes);
    }

    // Add drag behavior with manual position updates
    let capturedPositions: Map<string, {x: number, y: number}> | null = null;
    let dragStartPosition: {x: number, y: number} | null = null;
    let hasMoved = false;

    const drag = d3.drag<SVGCircleElement, VisualizationNode>()
      .on('start', (event, d: any) => {
        // Pin the node
        d.fx = d.x;
        d.fy = d.y;

        // Track starting position to detect actual movement
        dragStartPosition = { x: event.x, y: event.y };
        hasMoved = false;

        // Capture ALL current node positions BEFORE any changes
        // This preserves autolayout positions when converting to manual layout
        capturedPositions = new Map(
          visibleNodes.map(node => [
            node.id,
            { x: node.x ?? 0, y: node.y ?? 0 }
          ])
        );
      })
      .on('drag', (event, d: any) => {
        // Detect if node has actually moved (not just a click)
        if (dragStartPosition && !hasMoved) {
          const dx = Math.abs(event.x - dragStartPosition.x);
          const dy = Math.abs(event.y - dragStartPosition.y);
          // Consider it a drag if moved more than 2 pixels
          hasMoved = dx > 2 || dy > 2;
        }

        // Update position
        d.fx = event.x;
        d.fy = event.y;
        d.x = event.x;
        d.y = event.y;

        // Manually update all visual elements that depend on this node
        nodeElements.filter((node: any) => node.id === d.id)
          .attr('cx', event.x)
          .attr('cy', event.y);

        const offset = getLabelOffset(d);
        labelElements.filter((node: any) => node.id === d.id)
          .attr('text-anchor', getLabelAnchor(d))
          .attr('x', event.x + offset.x)
          .attr('y', event.y + offset.y);

        // Update connected links
        linkElements
          .attr('x1', (link: any) => link.source.x)
          .attr('y1', (link: any) => link.source.y)
          .attr('x2', (link: any) => link.target.x)
          .attr('y2', (link: any) => link.target.y);

        linkLabelElements
          .attr('x', (link: any) => (link.source.x + link.target.x) / 2)
          .attr('y', (link: any) => (link.source.y + link.target.y) / 2);
      })
      .on('end', (event, d: any) => {
        // Only update layout if there was actual dragging movement
        // Simple clicks shouldn't convert autolayout to manual layout
        if (hasMoved) {
          // Keep the node pinned
          d.fx = event.x;
          d.fy = event.y;

          // Update layout coordinates in infrastructure data
          // This converts autolayout nodes to fixed manual layout on drag
          // Pass captured positions to preserve autolayout for other nodes
          updateNodeLayout(d.id, event.x, event.y, capturedPositions);
        } else {
          // No movement - restore original position (was just a click)
          d.fx = null;
          d.fy = null;
        }

        // Clear captured positions after use
        capturedPositions = null;
        dragStartPosition = null;
        hasMoved = false;
      });

    (nodeElements as any).call(drag);

    // Stop simulation after a reasonable time to prevent continuous movement
    const timeoutId = setTimeout(() => {
      try {
        if (simulation) {
          const alpha = simulation.alpha();
          if (alpha > 0) {
            simulation.alpha(0);
          }
          // Always reset state in timeout since simulation won't fire 'end' event after stop()
          if (currentSimulationId === simulationIdRef.current) {
            setSimulationRunning(false);
          }
        }
      } catch (error) {
        console.warn('Simulation timeout error:', error);
      }
    }, 2000);

    // Cleanup
    return () => {
      try {
        clearTimeout(timeoutId);
        if (simulationRef.current) {
          simulationRef.current.stop();
          simulationRef.current = null;
          setSimulationRunning(false);
        }
      } catch (error) {
        console.warn('Cleanup error:', error);
      }
      visibleNodesRef.current = [];
      visibleLinksRef.current = [];
      nodeSelectionRef.current = null;
      linkSelectionRef.current = null;
      labelSelectionRef.current = null;
      linkLabelSelectionRef.current = null;
      containerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodes, links, selectedNode, currentContext,
    showNodeTypes, showConnectionTypes, width, height,
    highlightFixedNodes, autoLayoutType
  ]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="visualization-canvas"
      style={{ border: '1px solid #ccc', background: '#f9f9f9' }}
    />
  );
};
